import base from "@/fstore";
import validation from "@/fstore/validation";
import moment from 'moment';
import Candidate from './Job/Candidate';

const db = base.db;
const collection = "job";
const model = db.collection(collection);

const template = {
  title: {
    type: "string",
    required: true
  },
  description: {
    type: "string",
    default: ""
  },
  position: {
    type: "string",
    default: ""
  },
  code: {
    type: "string",
    default: ""
  },
  skills: {
    type: "object",
    default: [],
  },
  compensation: {
    type: "object",
    default: {
      range: "",
      rate: "",
      addons:[],
      other: null
    }
  },
  benefits: {
    type: "object",
    default: []
  },
  isRemote: {
    type: 'boolean',
    default: false
  },
  location: {
    type: "object",
    required: true
  },
  created: {
    type: "object",
    default: new Date()
  },
  questions: {
    type: "object",
    default: [
      {
        type: 'short_answer',
        question: '',
        answer: '',
        options: [],
        user_answer: ''
      }
    ]
  },
  company: {
    desc: "A reference to the company this job belongs to",
    type: "object",
    required: true
  },
  user: {
    desc: "A reference to the user who created the job.",
    type: "object",
    required: true
  },
  removedCandidates: {
    desc: "A list of candidate ids the company isn't interested in sending questionnaires to.",
    type: "object",
    default: []
  },
  published: {
    desc:
      "A timestamp of when the job was published. null if it hasn't been published yet.",
    type: "object",
    default: null
  },
  closed: {
    desc:
      "The date the position was marked by the company as closed. This is either a date object, or null",
    default: null
  },
  closedBy: {
    type: 'object',
    desc:
      "The user who closed the job",
    default: {
      ref: null,
      name: null
    }
  }
};

const generateRef = async (collection, docid) => {
  return await db.doc(`${collection}/${docid}`);
}

// retrieve all jobs in an array of references
const getData = refs => {
  const jobs = [];
  if (!refs.docs.length) {
    return jobs;
  }

  refs.docs.forEach(doc => {
    let job = null;
    if (doc.exists) {
      job = doc.data();
      if (!job.uid) job.uid = doc.id;
    }

    if (job !== null) jobs.push(job);
  });

  return jobs;
};



const create = async ({ companyId, userId }, data) => {
  // get a reference to the userdoc
  data.user = await db.doc(`userdata/${userId}`);
  data.company = await db.doc(`company/${companyId}`);

  const response = await validation.validateModel(model, data, template);
  if (!response.errors.length) {
    const newDoc = await model.add(response.doc);
    response.doc.uid = newDoc.id
  }

  return response;
};

const getRef = async (jobId) => {
  return await db.doc(`job/${jobId}`);
}

const getAllJobTitles = async () => {
  let refs = await model.get();
  const positions = []

  if (!refs.docs.length)
    return positions;

  refs.docs.forEach(doc => {
    doc = doc.data();

    if (!positions.some(position => position === doc.title)) {
      positions.push(doc.title);
    }
  });

  return positions;
}

const getById = async jobId => {
  let job = await model.doc(jobId).get();

  if (!job.exists) {
    return new Error({ code: 404, message: `Job ${jobId} was not found` });
  }

  job = job.data();

  if (!job.uid) job.uid = jobId;

  return job;
};

// retrieves all jobs associated with a company
const getAllByCompany = async companyId => {
  // get the company ref
  const company = await db.doc(`company/${companyId}`);
  const refs = await model.where("company", "==", company).orderBy('created', 'desc').get();
  const jobs = getData(refs);

  //console.log(jobs);
  return jobs;
};

// retrieves a list of jobs by the company for the given month
const getForCurrentMonth = async companyId => {
  // const date = moment();
  const date = new Date();
  const currentMonth = new Date(date.getFullYear(), date.getMonth(), 1);

  const company = await db.doc(`company/${companyId}`);
  const refs = await model.where("company", "==", company).where("created", ">=", currentMonth).get();
  // const refs = await model.where("company", "==", company).get();

  const jobs = getData(refs);
  return jobs;
}

// retrieves all jobs created by a specified user
const getAllByUser = async userId => {
  // get the user ref
  const user = await db.doc(`userdata/${userId}`);
  const refs = await model.where("user", "==", user).get();
  const jobs = getData(refs);

  return jobs;
};

const _getNullValueForField = (field) => {
  switch (template[field].type) {
    case "string":
      return "";
    case "object":
      return {};
    default:
      return null;
  }
}

const getTemplate = () => {
  const data = {}
  for (const field in template) {
    if (template[field].required)
      data[field] = _getNullValueForField(field);
    else
      data[field] = template[field].default;
  }

  return data;
}

const update = async (jobId, data) => {
  await model.doc(jobId).set({ ...data }, { merge: true });
}

const deleteById = async (uid) => {
  await model.doc(uid).delete();
}

const close = async(uid, closedByRef, closedByName) => {
  await update(uid, {
    closed: new Date(),
    closedBy: {
      ref: closedByRef,
      name: closedByName,
    }
  });  
}


export default {
  model,
  getTemplate,
  create,
  getRef,
  getById,
  getAllJobTitles,
  getAllByCompany,
  getAllByUser,
  getForCurrentMonth,
  update,
  deleteById,
  close
};
